import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Camera,
  Image as ImageIcon,
  Maximize2,
  Wand2,
  Loader2,
  CloudUpload,
  Check,
  AlertCircle,
  Coins,
  Home,
  Crown,
  Factory,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  X,
  Trash2,
  Info,
} from 'lucide-react';
import { StepIndicator } from '@/components/StepIndicator';
import { RoomTypeSelector } from '@/components/ui/RoomTypeSelector';
import { UploadedImage, RoomType, StagingStyle } from '@/types';
import { supportsStaging, STAGING_STYLES, getRoomLabel } from '@/lib/roomConfig';
import { formatCredits } from '@/lib/utils';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import HeicSafeImage from "@/components/ui/HeicSafeImage";
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { uploadModeForRoom, emptyRoomCount } from '@/lib/stagingFlow';

const STYLE_ICONS = { Home, Crown, Factory } as const;

type GenerationMode = 'both' | 'staged-only' | 'empty-only' | 'none';

const getGenerationModeLabel = (mode: GenerationMode): string => {
  switch (mode) {
    case 'both': return 'Staging + Leer';
    case 'staged-only': return 'Nur Staging';
    case 'empty-only': return 'Nur Leer';
    case 'none': return 'Übersprungen';
  }
};

interface UploadViewProps {
  uploadedImages: UploadedImage[];
  projectName?: string;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFilesDrop?: (files: File[]) => void;
  onUpdateImageMeta: (id: string, updates: Partial<UploadedImage>) => void;
  onDeleteImage?: (id: string) => void;
  onStartGeneration: () => void;
  onImageClick: (url: string) => void;
  credits?: number;
  onBuyCredits?: () => void;
  stagingStyle?: StagingStyle;
  onStagingStyleChange?: (style: StagingStyle) => void;
  stagingV2Active?: boolean;
}

export const UploadView: React.FC<UploadViewProps> = ({
  uploadedImages,
  projectName,
  onImageUpload,
  onFilesDrop,
  onUpdateImageMeta,
  onDeleteImage,
  onStartGeneration,
  onImageClick,
  credits = 0,
  onBuyCredits,
  stagingStyle = 'modern',
  onStagingStyleChange,
  stagingV2Active = false,
}) => {
  const { user } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [showRoomHint, setShowRoomHint] = useState(false);
  const [roomHintShown, setRoomHintShown] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('user_onboarding')
      .select('room_type_hint_shown')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        setRoomHintShown(data?.room_type_hint_shown ?? false);
      });
  }, [user?.id]);

  const markHintShown = useCallback(async () => {
    setRoomHintShown(true);
    if (!user?.id) return;
    await supabase
      .from('user_onboarding')
      .update({ room_type_hint_shown: true, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);
  }, [user?.id]);

  // Per-card expand override. Key = image.id, value = user-forced open/closed.
  // If a card has no entry, the default is: expanded while no room type is chosen, collapsed once set.
  const [expandOverride, setExpandOverride] = useState<Map<string, boolean>>(new Map());

  // Remembers the mode before an image was deselected, so it can be restored on re-select
  const savedModesRef = useRef<Map<string, GenerationMode>>(new Map());

  const isImageExpanded = useCallback((img: UploadedImage): boolean => {
    const override = expandOverride.get(img.id);
    if (override !== undefined) return override;
    return !img.roomType || img.roomType === RoomType.CUSTOM;
  }, [expandOverride]);

  const toggleImage = useCallback((imgId: string) => {
    setExpandOverride(prev => {
      const next = new Map(prev);
      const img = uploadedImages.find(i => i.id === imgId);
      const current = prev.get(imgId) ?? (img ? (!img.roomType || img.roomType === RoomType.CUSTOM) : false);
      next.set(imgId, !current);
      return next;
    });
  }, [uploadedImages]);

  const setImageExpanded = useCallback((imgId: string, expanded: boolean) => {
    setExpandOverride(prev => {
      const next = new Map(prev);
      next.set(imgId, expanded);
      return next;
    });
  }, []);

  const anyExpanded = uploadedImages.some(img => isImageExpanded(img));

  const toggleAll = useCallback(() => {
    const shouldExpand = !anyExpanded;
    setExpandOverride(() => {
      const next = new Map<string, boolean>();
      for (const img of uploadedImages) next.set(img.id, shouldExpand);
      return next;
    });
  }, [anyExpanded, uploadedImages]);

  const handleToggleSelection = useCallback((img: UploadedImage) => {
    const canStage = supportsStaging(img.roomType as string);
    const currentMode: GenerationMode = (img.generationMode ?? (canStage ? 'both' : 'empty-only')) as GenerationMode;
    if (currentMode === 'none') {
      // Re-select: restore saved mode or fall back to default
      const restored = savedModesRef.current.get(img.id) ?? (canStage ? 'both' : 'empty-only') as GenerationMode;
      onUpdateImageMeta(img.id, { generationMode: restored });
      savedModesRef.current.delete(img.id);
    } else {
      // Deselect: save current mode, set to none, collapse just this card
      savedModesRef.current.set(img.id, currentMode);
      onUpdateImageMeta(img.id, { generationMode: 'none' });
      if (img.roomType) setImageExpanded(img.id, false);
    }
  }, [onUpdateImageMeta, setImageExpanded]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name)
    );
    if (droppedFiles.length > 0 && onFilesDrop) onFilesDrop(droppedFiles);
  }, [onFilesDrop]);

  const isUploading = uploadedImages.some(img => img.isUploading);
  const hasUploadErrors = uploadedImages.some(img => img.uploadError);
  const allUploaded = uploadedImages.length > 0 && uploadedImages.every(img => img.storagePath && !img.isUploading);

  const estimatedCreditsNeeded = uploadedImages.reduce((total, img) => {
    const canStage = supportsStaging(img.roomType as string);
    const mode = img.generationMode || (canStage ? 'both' : 'empty-only');
    switch (mode) {
      case 'both': return total + 2;
      case 'staged-only': case 'empty-only': return total + 1;
      default: return total;
    }
  }, 0);
  const hasEnoughCredits = credits >= estimatedCreditsNeeded;

  const hasStagingRooms = uploadedImages.some(img => supportsStaging(img.roomType as string));
  const anySelected = uploadedImages.some(img => {
    const canStage = supportsStaging(img.roomType as string);
    const mode = img.generationMode ?? (canStage ? 'both' : 'empty-only');
    return mode !== 'none';
  });

  const activeImages = uploadedImages.filter(img => {
    const canStage = supportsStaging(img.roomType as string);
    const mode = img.generationMode ?? (canStage ? 'both' : 'empty-only');
    return mode !== 'none';
  });
  // All active images still have the auto-assigned default (LIVING) — user hasn't explicitly chosen room types
  const allActiveAtDefaultRoom = activeImages.length > 0 && activeImages.every(img => img.roomType === RoomType.LIVING);

  const handleStartGeneration = useCallback(() => {
    if (allActiveAtDefaultRoom && !roomHintShown) {
      setShowRoomHint(true);
      return;
    }
    onStartGeneration();
  }, [allActiveAtDefaultRoom, roomHintShown, onStartGeneration]);

  const handleHintContinue = useCallback(() => {
    markHintShown();
    setShowRoomHint(false);
    onStartGeneration();
  }, [markHintShown, onStartGeneration]);

  const handleHintBack = useCallback(() => {
    markHintShown();
    setShowRoomHint(false);
  }, [markHintShown]);

  return (
    <div className="space-y-8 animate-fade-in">
      <StepIndicator currentStep="upload" />

      <Card
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`transition-colors ${isDragging ? 'ring-2 ring-primary border-primary bg-primary/5' : ''}`}
      >
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Upload & Kategorisieren</h2>
              {projectName && (
                <p className="text-primary font-medium text-sm mt-1">{projectName}</p>
              )}
              <p className="text-muted-foreground mt-1">
                Lade Fotos hoch, überprüfe die Raumtypen und stelle sicher, dass alle persönlichen Gegenstände entfernt werden.
              </p>
            </div>
            <Button variant="secondary" className="cursor-pointer" asChild>
              <label>
                <Camera className="w-5 h-5 mr-2" /> Fotos hinzufügen
                <input type="file" multiple className="hidden" accept="image/*,.heic,.heif" onChange={onImageUpload} />
              </label>
            </Button>
          </div>

          {/* Staging style selector — shown above images when applicable */}
          {uploadedImages.length > 0 && hasStagingRooms && onStagingStyleChange && (
            <div className="mb-6 p-4 bg-muted/30 rounded-xl border border-border">
              <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Einrichtungsstil
              </Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {STAGING_STYLES.map((style) => {
                  const IconComponent = STYLE_ICONS[style.icon as keyof typeof STYLE_ICONS];
                  const isSelected = stagingStyle === style.id;
                  return (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => onStagingStyleChange(style.id)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'
                      }`}
                    >
                      <IconComponent className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                        {style.label}
                      </span>
                      <span className="text-[10px] text-muted-foreground text-center leading-tight">
                        {style.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {uploadedImages.length === 0 ? (
            <label className={`block text-center py-20 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${
              isDragging
                ? 'bg-primary/10 border-primary text-primary'
                : 'bg-muted/30 border-muted text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/50'
            }`}>
              <CloudUpload className={`w-12 h-12 mx-auto mb-3 ${isDragging ? 'text-primary' : 'opacity-50'}`} />
              <p className="font-medium">
                {isDragging ? 'Bilder hier ablegen' : 'Fotos hierher ziehen oder klicken'}
              </p>
              <p className="text-sm mt-1 opacity-70">JPEG, PNG, HEIC</p>
              <input type="file" multiple className="hidden" accept="image/*,.heic,.heif" onChange={onImageUpload} />
            </label>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {uploadedImages.length} Bild{uploadedImages.length === 1 ? '' : 'er'} · Klicke auf eine Karte zum Ein- oder Ausklappen
                </p>
                <button
                  type="button"
                  onClick={toggleAll}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-muted text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors"
                  title={anyExpanded ? 'Alle Karten einklappen' : 'Alle Karten ausklappen'}
                >
                  {anyExpanded ? <ChevronsDownUp className="w-3.5 h-3.5" /> : <ChevronsUpDown className="w-3.5 h-3.5" />}
                  <span>{anyExpanded ? 'Alle einklappen' : 'Alle ausklappen'}</span>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
              {[...uploadedImages].sort((a, b) => {
                const canStageA = supportsStaging(a.roomType as string);
                const canStageB = supportsStaging(b.roomType as string);
                const aSkipped = (a.generationMode ?? (canStageA ? 'both' : 'empty-only')) === 'none';
                const bSkipped = (b.generationMode ?? (canStageB ? 'both' : 'empty-only')) === 'none';
                return Number(aSkipped) - Number(bSkipped);
              }).map((img) => {
                const canStage = supportsStaging(img.roomType as string);
                const currentMode: GenerationMode = (img.generationMode ?? (canStage ? 'both' : 'empty-only')) as GenerationMode;
                const isSkipped = currentMode === 'none';
                const isConfigured = !!img.roomType;
                const isExpanded = isImageExpanded(img);
                const roomLabel = img.roomType === RoomType.CUSTOM && img.customLabel
                  ? img.customLabel
                  : getRoomLabel(img.roomType as string);

                return (
                  <Card
                    key={img.id}
                    className={`overflow-hidden transition-all duration-200 ${
                      isSkipped ? 'opacity-60' : 'hover:shadow-md'
                    }`}
                  >
                    {/* Image thumbnail */}
                    <div
                      className="relative aspect-[4/3] bg-muted rounded-t-xl overflow-hidden cursor-pointer group"
                      onClick={() => onImageClick(img.previewUrl)}
                    >
                      <HeicSafeImage
                        src={img.previewUrl}
                        className="w-full h-full object-cover"
                        alt="Preview"
                      />

                      {img.isUploading && (
                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                          <Loader2 className="w-8 h-8 text-white animate-spin mb-2" />
                          <span className="text-white text-sm font-medium">Hochladen...</span>
                        </div>
                      )}

                      {img.uploadError && (
                        <div className="absolute inset-0 bg-destructive/80 flex flex-col items-center justify-center p-4">
                          <AlertCircle className="w-8 h-8 text-white mb-2" />
                          <span className="text-white text-sm font-medium text-center">{img.uploadError}</span>
                        </div>
                      )}

{img.isTenantUpload ? (
                        <Badge className="absolute top-2 left-2 bg-violet-500 hover:bg-violet-600">
                          Vom Mieter
                        </Badge>
                      ) : img.isFromDatabase ? (
                        <Badge className="absolute top-2 left-2 bg-blue-500 hover:bg-blue-600">
                          Vorhanden
                        </Badge>
                      ) : img.storagePath && !img.isUploading && !img.uploadError ? (
                        <Badge className="absolute top-2 left-2 bg-emerald-500 hover:bg-emerald-600 gap-1">
                          <Check className="w-3 h-3" /><span>Hochgeladen</span>
                        </Badge>
                      ) : null}

                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none">
                        <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                      </div>

                      {onDeleteImage && (
                        <div className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
                          {pendingDeleteId === img.id ? (
                            <div className="flex items-center gap-1 bg-black/70 rounded-lg px-2 py-1">
                              <span className="text-white text-xs">Löschen?</span>
                              <button
                                type="button"
                                onClick={() => { onDeleteImage(img.id); setPendingDeleteId(null); }}
                                className="text-xs font-medium text-red-400 hover:text-red-300 px-1"
                              >
                                Ja
                              </button>
                              <button
                                type="button"
                                onClick={() => setPendingDeleteId(null)}
                                className="text-xs font-medium text-white/70 hover:text-white px-1"
                              >
                                Nein
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setPendingDeleteId(img.id)}
                              className="p-1.5 rounded-lg bg-black/50 text-white/80 hover:bg-destructive hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <CardContent className="p-4">
                      {/* Header row: selection checkbox + summary/label + collapse chevron */}
                      <div className="flex items-center gap-2.5 mb-3">
                        {/* Staging selection toggle — hover previews the opposite state */}
                        <button
                          type="button"
                          onClick={() => handleToggleSelection(img)}
                          className={`group flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all cursor-pointer ${
                            isSkipped
                              ? 'border-dashed border-muted-foreground/40 text-muted-foreground hover:border-primary hover:bg-primary/10 hover:text-primary'
                              : 'border-primary/40 bg-primary/10 text-primary hover:bg-destructive/10 hover:border-destructive/50 hover:text-destructive'
                          }`}
                        >
                          {isSkipped ? (
                            <><X className="w-3 h-3" /><span>Auswählen</span></>
                          ) : (
                            <>
                              <Check className="w-3 h-3 group-hover:hidden" />
                              <X className="w-3 h-3 hidden group-hover:block" />
                              <span className="group-hover:hidden">Ausgewählt</span>
                              <span className="hidden group-hover:inline">Abwählen</span>
                            </>
                          )}
                        </button>

                        {/* Summary row (clickable to expand) when configured, placeholder when not */}
                        {isConfigured ? (
                          <button
                            type="button"
                            onClick={() => toggleImage(img.id)}
                            className="flex-1 flex items-center justify-between text-left min-w-0"
                          >
                            <span className="text-sm font-medium text-foreground truncate">
                              {roomLabel}
                              <span className="text-muted-foreground font-normal"> · {getGenerationModeLabel(currentMode)}</span>
                            </span>
                            <ChevronDown
                              className={`w-4 h-4 text-muted-foreground flex-shrink-0 ml-1 transition-transform duration-200 ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                        ) : (
                          <span className="text-sm text-muted-foreground">Raumtyp wählen…</span>
                        )}
                      </div>

                      {/* Expandable settings: animated via CSS grid-template-rows trick.
                          Unconfigured cards are always expanded; configured cards honor isExpanded. */}
                      <div
                        aria-hidden={isConfigured && !isExpanded}
                        className={`grid transition-all duration-300 ease-out ${
                          !isConfigured || isExpanded
                            ? 'grid-rows-[1fr] opacity-100'
                            : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                        <div className="space-y-3">
                          {/* Room type */}
                          <div>
                            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                              Raumtyp
                            </span>
                            <RoomTypeSelector
                              value={img.roomType}
                              onChange={(t) => {
                                onUpdateImageMeta(img.id, { roomType: t });
                                // Stay expanded so the user can verify/change the default generation mode.
                                setImageExpanded(img.id, true);
                              }}
                              customValue={img.customLabel}
                              onCustomChange={(s) => onUpdateImageMeta(img.id, { customLabel: s })}
                            />
                          </div>

                          {/* "Küche behalten" — only for kitchen */}
                          {img.roomType === RoomType.KITCHEN && (
                            <div className="flex items-center space-x-2 border p-2 rounded-lg bg-muted/20">
                              <Switch
                                id={`keep-kitchen-${img.id}`}
                                checked={img.keepKitchen}
                                onCheckedChange={(checked) => onUpdateImageMeta(img.id, { keepKitchen: checked })}
                              />
                              <div className="flex flex-col">
                                <Label htmlFor={`keep-kitchen-${img.id}`} className="text-sm font-medium cursor-pointer">
                                  Küche behalten?
                                </Label>
                                <span className="text-[10px] text-muted-foreground">
                                  Wenn deaktiviert, wird sie geleert.
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Generation mode */}
                          <div>
                            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                              {stagingV2Active ? 'Bearbeitung' : 'Generierung'}
                            </span>
                            {stagingV2Active ? (
                              <div className="flex items-center space-x-2 border p-2 rounded-lg bg-muted/20 mt-1.5">
                                <Switch
                                  id={`skip-${img.id}`}
                                  checked={img.generationMode === 'none'}
                                  onCheckedChange={(checked) => {
                                    onUpdateImageMeta(img.id, { generationMode: uploadModeForRoom(checked) });
                                    if (img.roomType) setImageExpanded(img.id, false);
                                  }}
                                />
                                <div className="flex flex-col">
                                  <Label htmlFor={`skip-${img.id}`} className="text-sm font-medium cursor-pointer">
                                    Raum überspringen
                                  </Label>
                                  <span className="text-[10px] text-muted-foreground">
                                    Aktiv: dieser Raum wird nicht leergeräumt.
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {(() => {
                                  const options = canStage
                                    ? [
                                        { value: 'both' as const, label: 'Staging + Leer', credits: 2 },
                                        { value: 'staged-only' as const, label: 'Nur Staging', credits: 1 },
                                        { value: 'empty-only' as const, label: 'Nur Leer', credits: 1 },
                                        { value: 'none' as const, label: 'Keine', credits: 0 },
                                      ]
                                    : [
                                        { value: 'empty-only' as const, label: 'Nur Leer', credits: 1 },
                                        { value: 'none' as const, label: 'Keine', credits: 0 },
                                      ];
                                  return options.map((opt) => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => {
                                        onUpdateImageMeta(img.id, { generationMode: opt.value });
                                        if (img.roomType) setImageExpanded(img.id, false);
                                      }}
                                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                        currentMode === opt.value
                                          ? 'border-primary bg-primary/10 text-primary font-medium'
                                          : 'border-muted text-muted-foreground hover:border-muted-foreground/40'
                                      }`}
                                    >
                                      {opt.label}
                                      <span className="ml-1 opacity-60">({opt.credits})</span>
                                    </button>
                                  ));
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              </div>
            </div>
          )}

          {/* Upload status bar */}
          {uploadedImages.length > 0 && (
            <div className="mt-6 p-4 bg-muted/30 rounded-xl border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isUploading ? (
                    <>
                      <CloudUpload className="w-5 h-5 text-primary animate-pulse" />
                      <span className="text-sm text-muted-foreground">Bilder werden zu Supabase hochgeladen...</span>
                    </>
                  ) : allUploaded ? (
                    <>
                      <Check className="w-5 h-5 text-emerald-500" />
                      <span className="text-sm text-emerald-500 font-medium">Alle Bilder erfolgreich hochgeladen</span>
                    </>
                  ) : hasUploadErrors ? (
                    <>
                      <AlertCircle className="w-5 h-5 text-destructive" />
                      <span className="text-sm text-destructive">Einige Bilder konnten nicht hochgeladen werden</span>
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{uploadedImages.length} Bilder ausgewählt</span>
                    </>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {uploadedImages.filter(img => img.storagePath).length} / {uploadedImages.length} hochgeladen
                </div>
              </div>
            </div>
          )}

          {stagingV2Active && (
            <p className="mt-8 text-xs text-muted-foreground text-center mb-2">
              {emptyRoomCount(uploadedImages, true, (i) => supportsStaging((i as UploadedImage).roomType as string))} Räume werden leergeräumt
              {' '}({emptyRoomCount(uploadedImages, true, (i) => supportsStaging((i as UploadedImage).roomType as string))} Credits).
              Einrichten wählst du danach pro Raum.
            </p>
          )}

          {anySelected && <div className="mt-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-muted px-3 py-2 rounded-lg">
                <Coins className="w-5 h-5 text-amber-500" />
                <span className="font-medium text-foreground">{formatCredits(credits)} Credits</span>
              </div>
              {uploadedImages.length > 0 && (
                <span className={`text-sm ${hasEnoughCredits ? 'text-muted-foreground' : 'text-destructive'}`}>
                  ~{estimatedCreditsNeeded} Credits benötigt
                </span>
              )}
            </div>

            <div className="flex gap-3">
              {!hasEnoughCredits && uploadedImages.length > 0 && onBuyCredits ? (
                <Button variant="outline" onClick={onBuyCredits} className="gap-2">
                  <Coins className="w-4 h-4" /> Guthaben aufladen
                </Button>
              ) : null}
              <Button
                onClick={handleStartGeneration}
                disabled={uploadedImages.length === 0 || isUploading || !allUploaded || !hasEnoughCredits}
                className="gap-2"
                title={!hasEnoughCredits ? 'Nicht genug Credits' : undefined}
              >
                {isUploading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Bilder werden hochgeladen...</>
                ) : !hasEnoughCredits ? (
                  <><AlertCircle className="w-5 h-5" /> Nicht genug Credits</>
                ) : (
                  <>Verarbeitung starten <Wand2 className="w-5 h-5" /></>
                )}
              </Button>
            </div>
          </div>}
        </CardContent>
      </Card>

      <Dialog open={showRoomHint} onOpenChange={(open) => { if (!open) handleHintBack(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-amber-500" />
              Raumtypen nicht ausgewählt
            </DialogTitle>
            <DialogDescription className="pt-1">
              Alle Bilder verwenden noch den Standard-Raumtyp „Wohnzimmer". Falls deine
              Bilder andere Räume zeigen, empfehlen wir, den Raumtyp für jedes Bild einzeln
              festzulegen – so erhältst du deutlich passendere Staging-Ergebnisse.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleHintBack}>
              Raumtypen festlegen
            </Button>
            <Button onClick={handleHintContinue}>
              Trotzdem generieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
